import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Typography } from '@material-ui/core';
import { ToastContainer, toast } from 'react-toastify';
import { consumerAlgorithmCalculation } from '../../../redux/actions/Firebase';
import ChartCalculations from './chartCalculations';
import PageContainer from '../../../myComponents/pageContainer';
import PageLoader from '../../../myComponents/loaders/pageLoader';

import Filters from '../../../myComponents/filters/Filters';
import UserDropdownHeader from '../../../myComponents/userDropdownHeader';
import PDFExportButton from '../../../myComponents/PDFExportButton';

import { findLatestExistingBatchData, batchDataSort } from '../../../helperFunctions';
import { DEFAULT_BATCH_COUNTRY } from '../../../config/batchConfig';
import BuyerBatchSelector from '../../../myComponents/buyerBatchSelector';

const ConsumerSample = () => {
  const dispatch = useDispatch();
  const appData = useSelector(({ data }) => data);
  const PDFContentRef = useRef(null);
  const [pickedBatchData, setPickedBatchData] = useState(null);
  const [pickedBatchCountry, setPickedBatchCountry] = useState(DEFAULT_BATCH_COUNTRY);
  const [chosenFilters, setChosenFilters] = useState(null);

  const [pageIsLoading, setPageIsLoading] = useState(true);
  const [useEffectShouldTrigger, setUseEffectShouldTrigger] = useState(true);

  //Calculation state
  const [calculations, setCalculations] = useState({
    loading: false,
    results: null,
  });

  useEffect(() => {
    if (!appData.loading && useEffectShouldTrigger) {
      setUseEffectShouldTrigger(false);
      if (appData.error) {
        showToastWarn();
      } else {
        const { latestQuarter, latestYear } = findLatestExistingBatchData(
          appData.combinedBatchDataCodes[DEFAULT_BATCH_COUNTRY],
        );
        const pickedBatchToSet = {
          quarter: [latestQuarter],
          year: latestYear,
        };
        setPickedBatchData(pickedBatchToSet);
        setPickedBatchCountry(DEFAULT_BATCH_COUNTRY);

        calculateHandler(chosenFilters, pickedBatchToSet, DEFAULT_BATCH_COUNTRY);
        setPageIsLoading(false);
      }
    }
  }, [appData.loading]);

  const calculateHandler = (filters, newPickedBatchData, newPickedBatchCountry) => {
    setCalculations(prevState => ({ ...prevState, loading: true }));
    consumerAlgorithmCalculation({ filters: filters, batches: newPickedBatchData, batchCountry: newPickedBatchCountry })
      .then(res => {
        setCalculations({ results: res, loading: false });
      })
      .catch(err => {
        setCalculations({ results: null, loading: false });
        showToastWarn();
      });
  };

  const onFiltersSubmit = preparedFilterData => {
    setChosenFilters(preparedFilterData);
    calculateHandler(preparedFilterData, pickedBatchData, pickedBatchCountry);
  };

  const onChangeBatchQuarter = event => {
    const {
      target: { value },
    } = event;

    // On autofill we get a stringified value.
    const valueToSet = typeof value === 'string' ? value.split(',') : value;
    if (!valueToSet.length) return;

    const batchInfoToSet = { ...pickedBatchData, quarter: valueToSet };

    setPickedBatchData(batchInfoToSet);
  };

  const onConfirmBatchQuarterChange = () => {
    //Run calculation on quarter selector close
    //Run algo, this change event cannot trigger if calculation type is buyer
    calculateHandler(chosenFilters, pickedBatchData, pickedBatchCountry);
  };

  const onChangeBatchCountry = event => {
    const {
      target: { value },
    } = event;

    const newestBatchYear = Object.keys(appData.combinedBatchDataCodes[value])
      .sort()
      .pop();
    const quarters = appData.combinedBatchDataCodes[value][newestBatchYear].sort(batchDataSort);
    const latestQuarter = quarters[quarters.length - 1];

    const batchInfoToSet = {
      quarter: [latestQuarter],
      year: newestBatchYear,
    };

    setPickedBatchData(batchInfoToSet);
    setPickedBatchCountry(value);

    //Run brand persona algo, this change event cannot trigger if calculation type is buyer
    calculateHandler(chosenFilters, batchInfoToSet, value);
  };

  const onChangeBatchYear = event => {
    const {
      target: { value },
    } = event;

    const quarters = appData.combinedBatchDataCodes[pickedBatchCountry][value].sort(batchDataSort);
    const latestQuarter = quarters[quarters.length - 1];

    const batchInfoToSet = {
      quarter: [latestQuarter],
      year: value,
    };

    setPickedBatchData(batchInfoToSet);

    //Run algo, this change event cannot trigger if calculation type is buyer
    calculateHandler(chosenFilters, batchInfoToSet, pickedBatchCountry);
  };

  const showToastWarn = () => {
    toast.warn('Something went wrong, please try again later.', {
      position: 'bottom-right',
      autoClose: 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  return (
    <PageContainer>
      {pageIsLoading || calculations.loading ? <PageLoader dashboardCalculation /> : null}
      {calculations.results ? (
        <div ref={PDFContentRef}>
          <UserDropdownHeader />

          <Box
            width="100%"
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            padding="20px 20px 0px 20px"
            style={{ background: '#fff' }}>
            <BuyerBatchSelector
              calculationType={'batch'}
              onChangeCalculationType={() => {}}
              pickedBuyerPersona={null}
              onChangeBuyerPersona={() => {}}
              pickedBatchData={pickedBatchData}
              pickedBatchCountry={pickedBatchCountry}
              onChangeBatchCountry={onChangeBatchCountry}
              onChangeBatchQuarter={onChangeBatchQuarter}
              onConfirmBatchQuarterChange={onConfirmBatchQuarterChange}
              onChangeBatchYear={onChangeBatchYear}
              type="consumer"
              withConsumerHeading
            />
          </Box>

          <Filters calculateHandler={onFiltersSubmit} themeColor="red" pickedBatchCountry={pickedBatchCountry} />
          {/* <PDFExportButton contentRef={PDFContentRef} type="red" /> */}
          {calculations.results.no_data ? (
            <Box display="flex" justifyContent="center" alignItems="center" marginTop="100px">
              <Typography
                variant="h6"
                color="textSecondary"
                style={{
                  fontSize: '20px',
                  margin: '0',
                  padding: '0',
                  textAlign: 'center',
                  fontWeight: 'bold',
                }}>
                No data, choose other "Consumer Settings" combination.
              </Typography>
            </Box>
          ) : (
            <ChartCalculations data={calculations.results} />
          )}
        </div>
      ) : null}
      <ToastContainer
        theme="colored"
        position="bottom-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </PageContainer>
  );
};

export default ConsumerSample;
